'use client';

import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { type CSSProperties, type JSX, memo, useMemo } from 'react';

export type TextShimmerProps = {
  children: string;
  as?: keyof typeof MotionComponents;
  className?: string;
  duration?: number;
  spread?: number;
};

const MotionComponents = {
  p: motion.create('p' as keyof JSX.IntrinsicElements),
} as const;

const ShimmerComponent = ({
  children,
  as: componentType = 'p',
  className,
  duration = 1,
  spread = 2,
}: TextShimmerProps) => {
  const dynamicSpread = useMemo(() => (children?.length ?? 0) * spread, [children, spread]);

  const MComponent = MotionComponents[componentType];
  return (
    <MComponent
      animate={{ backgroundPosition: '0% center' }}
      className={cn(
        'relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent',
        '[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--color-background),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]',
        className
      )}
      initial={{ backgroundPosition: '100% center' }}
      style={
        {
          '--spread': `${dynamicSpread}px`,
          backgroundImage:
            'var(--bg), linear-gradient(var(--color-muted-foreground), var(--color-muted-foreground))',
        } as CSSProperties
      }
      transition={{
        repeat: Number.POSITIVE_INFINITY,
        duration,
        ease: 'linear',
      }}
    >
      {children}
    </MComponent>
  );
};

export const Shimmer = memo(ShimmerComponent);
